// components/TechnicianTimeOffModal.jsx
import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const TechnicianTimeOffModal = ({ isOpen, onClose, technicians, onSubmit }) => {
  const [formData, setFormData] = useState({
    technicianId: '',
    absenceType: 'vacation',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    reason: '',
    isAllDay: true,
    isRecurring: false,
    recurringType: 'weekly'
  });

  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  const absenceTypes = [
    { value: 'vacation', label: 'Congés' },
    { value: 'sick', label: 'Maladie' },
    { value: 'authorization', label: 'Autorisation' },
    { value: 'training', label: 'Formation' },
    { value: 'meeting', label: 'Réunion' },
    { value: 'personal', label: 'Raison personnelle' },
    { value: 'other', label: 'Autre' }
  ];

  const recurringTypes = [
    { value: 'daily', label: 'Tous les jours' },
    { value: 'weekly', label: 'Toutes les semaines' },
    { value: 'biweekly', label: 'Toutes les 2 semaines' },
    { value: 'monthly', label: 'Tous les mois' }
  ];

  const technicianOptions = technicians?.map(tech => ({
    value: tech?.id,
    label: `${tech?.name} - ${tech?.specialty}`
  })) || [];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.technicianId) newErrors.technicianId = 'Technicien requis';
    if (!formData?.absenceType) newErrors.absenceType = 'Type d\'absence requis';
    if (!formData?.startDate) newErrors.startDate = 'Date de début requise';
    
    // Si ce n'est pas toute la journée, vérifier les heures
    if (!formData?.isAllDay) {
      if (!formData?.startTime) newErrors.startTime = 'Heure de début requise';
      if (!formData?.endTime) newErrors.endTime = 'Heure de fin requise';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit({
        ...formData,
        id: Date.now(), // ID temporaire, sera remplacé par la BDD
        color: 'error', // Rouge pour les absences
        title: getAbsenceTitle(),
        status: 'timeoff'
      });
      onClose();
    }
  };

  const getAbsenceTitle = () => {
    const tech = technicians.find(t => t.id === formData.technicianId);
    const type = absenceTypes.find(t => t.value === formData.absenceType)?.label;
    return `${tech?.name} - ${type}`;
  };

  const formatDateForDisplay = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="bg-card rounded-lg border border-border shadow-lg w-full max-w-2xl max-h-[90vh] overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Gestion des absences</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Marquer l'indisponibilité d'un technicien
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Fermer"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-240px)] scrollbar-thin">
          <div className="space-y-6">
            {/* Sélection du technicien */}
            <div>
              <h3 className="font-medium text-foreground mb-4">Technicien concerné</h3>
              <Select
                label="Sélectionner un technicien"
                required
                options={technicianOptions}
                value={formData?.technicianId}
                onChange={(value) => handleChange('technicianId', value)}
                error={errors?.technicianId}
                searchable
                placeholder="Choisir un technicien..."
              />
            </div>

            {/* Type d'absence */}
            <div>
              <h3 className="font-medium text-foreground mb-4">Type d'absence</h3>
              <Select
                label="Type"
                required
                options={absenceTypes}
                value={formData?.absenceType}
                onChange={(value) => handleChange('absenceType', value)}
                error={errors?.absenceType}
              />
            </div>

            {/* Période d'absence */}
            <div>
              <h3 className="font-medium text-foreground mb-4">Période d'absence</h3>
              
              {/* Absence toute la journée */}
              <div className="mb-4">
                <Checkbox
                  label="Toute la journée"
                  description="Cocher si l'absence dure toute la journée"
                  checked={formData?.isAllDay}
                  onChange={(e) => handleChange('isAllDay', e?.target?.checked)}
                />
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <Input
                  label="Date de début"
                  type="date"
                  required
                  value={formData?.startDate}
                  onChange={(e) => handleChange('startDate', e?.target?.value)}
                  error={errors?.startDate}
                  min={new Date().toISOString().split('T')[0]}
                />
                <Input
                  label="Date de fin"
                  type="date"
                  value={formData?.endDate}
                  onChange={(e) => handleChange('endDate', e?.target?.value)}
                  min={formData?.startDate || new Date().toISOString().split('T')[0]}
                />
              </div>

              {/* Heures (si pas toute la journée) */}
              {!formData?.isAllDay && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Heure de début"
                    type="time"
                    required
                    value={formData?.startTime}
                    onChange={(e) => handleChange('startTime', e?.target?.value)}
                    error={errors?.startTime}
                  />
                  <Input
                    label="Heure de fin"
                    type="time"
                    required
                    value={formData?.endTime}
                    onChange={(e) => handleChange('endTime', e?.target?.value)}
                    error={errors?.endTime}
                  />
                </div>
              )}
            </div>

            {/* Absence récurrente */}
            <div>
              <div className="mb-4">
                <Checkbox
                  label="Absence récurrente"
                  description="Se répète chaque semaine / mois"
                  checked={formData?.isRecurring}
                  onChange={(e) => handleChange('isRecurring', e?.target?.checked)}
                />
              </div>

              {formData?.isRecurring && (
                <Select
                  label="Répéter"
                  options={recurringTypes}
                  value={formData?.recurringType}
                  onChange={(value) => handleChange('recurringType', value)}
                />
              )}
            </div>

            {/* Motif */}
            <div>
              <Input
                label="Motif (optionnel)"
                type="text"
                value={formData?.reason}
                onChange={(e) => handleChange('reason', e?.target?.value)}
                placeholder="Précisez le motif de l'absence..."
              />
            </div>

            {/* Aperçu */}
            {formData?.technicianId && formData?.startDate && (
              <div className="p-4 bg-error/10 rounded-lg border border-error/20">
                <h4 className="text-sm font-medium text-error mb-2">Aperçu dans le calendrier</h4>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-error" />
                  <span className="text-sm text-foreground">
                    {getAbsenceTitle()}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {formData?.isAllDay 
                    ? `Le ${formatDateForDisplay(formData.startDate)}${formData.endDate ? ` au ${formatDateForDisplay(formData.endDate)}` : ''}`
                    : `Le ${formatDateForDisplay(formData.startDate)} de ${formData.startTime} à ${formData.endTime}`
                  }
                  {formData?.isRecurring && ` (Récurrent: ${recurringTypes.find(t => t.value === formData.recurringType)?.label})`}
                </p>
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Annuler
          </Button>
          <Button
            variant="default"
            iconName="CalendarX"
            onClick={handleSubmit}
            className="bg-error hover:bg-error/90"
          >
            Marquer comme indisponible
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TechnicianTimeOffModal;